import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, ShieldCheck } from 'lucide-react';
import { companyInfo } from '../data';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-neutral-950 text-white pt-16 pb-8 border-t border-neutral-900 overflow-hidden">
      {/* Background Image with a working mason */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-45"
        style={{ backgroundImage: `url('https://assets.auxodev.dk/assets/58aac8da-8467-43e1-8dec-94cd5cd444a3/25bedf53-9645-405d-b81b-6018fb581e47.jpg')` }}
      />
      {/* Dark Overlay for superior readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-neutral-950/75 via-neutral-950/55 to-neutral-950/75 pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        {/* Column 1: About & Trust */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col">
            <span className="text-lg font-extrabold tracking-tight text-white flex items-center gap-1.5">
              <span className="text-red-600">PEDERSEN</span>
              <span className="font-light text-neutral-300">BYG</span>
            </span>
            <span className="text-[9px] uppercase tracking-[0.15em] text-neutral-500 font-semibold -mt-1">
              v/ Christian Juhl Pedersen
            </span>
          </div>
          <p className="text-sm text-white leading-relaxed">
            {companyInfo.tagline}. Din lokale murermester i Langå, Randers og omegn.
          </p>
        </div>

        {/* Column 2: Quick Links */}
        <div className="flex flex-col gap-4">
          <h3 className="text-white font-bold text-sm uppercase tracking-wider border-b border-neutral-800 pb-2">
            Hurtige links
          </h3>
          <ul className="flex flex-col gap-2.5 text-sm">
            <li>
              <Link to="/" className="hover:text-neutral-300 transition-colors">Forside</Link>
            </li>
            <li>
              <Link to="/ydelser" className="hover:text-neutral-300 transition-colors">Ydelser</Link>
            </li>
            <li>
              <Link to="/profil" className="hover:text-neutral-300 transition-colors">Profil & Om mig</Link>
            </li>
            <li>
              <Link to="/kontakt" className="hover:text-neutral-300 transition-colors">Kontakt & Tilbud</Link>
            </li>
          </ul>
        </div>

        {/* Column 3: Contact Info */}
        <div className="flex flex-col gap-4">
          <h3 className="text-white font-bold text-sm uppercase tracking-wider border-b border-neutral-800 pb-2">
            Kontakt mig
          </h3>
          <ul className="flex flex-col gap-3 text-sm">
            <li className="flex items-start gap-2.5">
              <MapPin size={16} className="text-white mt-0.5 flex-shrink-0" />
              <span>{companyInfo.address}</span>
            </li>
            <li className="flex items-center gap-2.5">
              <Phone size={16} className="text-white flex-shrink-0" />
              <a href={`tel:${companyInfo.phone.replace(/\s+/g, '')}`} className="hover:text-neutral-300 transition-colors">
                {companyInfo.phone}
              </a>
            </li>
            <li className="flex items-center gap-2.5">
              <Mail size={16} className="text-white flex-shrink-0" />
              <a href={`mailto:${companyInfo.email}`} className="hover:text-neutral-300 transition-colors">
                {companyInfo.email}
              </a>
            </li>
            <li className="text-xs text-neutral-400 mt-1">
              CVR-nr: {companyInfo.cvr}
            </li>
          </ul>
        </div>

        {/* Column 4: Opening Hours */}
        <div className="flex flex-col gap-4">
          <h3 className="text-white font-bold text-sm uppercase tracking-wider border-b border-neutral-800 pb-2">
            Åbningstider
          </h3>
          <ul className="flex flex-col gap-2.5 text-sm">
            {companyInfo.openingHours.map((item, idx) => (
              <li key={idx} className="flex justify-between items-center border-b border-neutral-900 pb-1.5 last:border-0">
                <span className="text-white">{item.days}</span>
                <span className="text-white font-medium text-xs">{item.hours}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 mt-12 pt-6 border-t border-neutral-900 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-neutral-300">
        <p>© {currentYear} {companyInfo.name}. Alle rettigheder forbeholdes.</p>
        <p className="flex items-center gap-1">
          Designet af <a href="https://auxo.dk" target="_blank" rel="noopener noreferrer">Auxo</a>
        </p>
      </div>
    </footer>
  );
}
